// src/pages/users/CreateUserPage.js
import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    Row,
    Col,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "../../components/Forms/PhoneInput";
import "../../assets/css/page-common.css";
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D";

const CreateUserPage = () => {
    const navigate = useNavigate();

    // Yetki & liste verileri
    const [userRoles, setUserRoles] = useState([]);
    const [loadingUserInfo, setLoadingUserInfo] = useState(true);

    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    // Form
    const [userType, setUserType] = useState("company"); // "company" | "account" | "super"
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        phoneNumber: "",
        companyId: "",
        accountId: "",
        username: "",
        email: "",
        password: "",
        roles: [],
        twoFaEnabled: false,
        enabled: true,
    });

    // Util
    const hasRole = (requiredRoles) => {
        if (!requiredRoles || requiredRoles.length === 0) return true;
        return requiredRoles.some((role) => userRoles.includes(role));
    };
    const canCreateSuperAdmin = () => hasRole(["ROLE_SUPER_ADMIN"]);

    const toTitleCaseTR = (s) =>
        s
            .toLocaleLowerCase("tr-TR")
            .replace(/(^|[\s\-'])(\p{L})/gu, (_, p1, p2) => p1 + p2.toLocaleUpperCase("tr-TR"));

    const getTahsilat360Id = () => {
        const tah = companies.find(
            (c) => String(c.companyCode) === "188129"
        );
        return tah ? tah.id : "";
    };

    const resetForm = () => {
        setForm({
            phoneNumber: "",
            companyId: "",
            accountId: "",
            username: "",
            email: "",
            password: "",
            roles: [],
            twoFaEnabled: false,
            enabled: true,
        });
        setUserType("company");
        setError(null);
    };

    // İlk yükleme
    useEffect(() => {
        const init = async () => {
            await Promise.all([fetchUserInfo(), fetchCompanies(), fetchAccounts()]);
            resetForm();
        };
        init();
    }, []);

    // userType değişince form alanlarını temizle / set et
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            companyId: userType === "super" ? getTahsilat360Id() : "",
            accountId: "",
            roles: userType === "super" ? ["ROLE_SUPER_ADMIN"] : [],
        }));
    }, [userType, companies]);

    // API çağrıları
    const fetchUserInfo = async () => {
        setLoadingUserInfo(true);
        try {
            const res = await axios.get(API.auth_me, {
                headers: {},
            });
            if (res.data?.statusCode === 200) {
                setUserRoles(res.data.data?.roles || []);
            }
        } catch (e) {
            if (e.response?.status === 401 || e.response?.status === 403) {
                toast.error("Yetki hatası. Lütfen tekrar giriş yapın.");
            }
        } finally {
            setLoadingUserInfo(false);
        }
    };

    const fetchCompanies = async () => {
        setLoadingCompanies(true);
        try {
            const res = await axios.get(API.get_companies_names, {
                headers: {},
            });
            setCompanies(res.data?.data || []);
        } catch {
            setCompanies([]);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const fetchAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const res = await axios.get(API.get_accounts_id_name, {
                headers: {},
            });
            setAccounts(res.data?.data || []);
        } catch {
            setAccounts([]);
        } finally {
            setLoadingAccounts(false);
        }
    };

    // Form yardımcıları
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setError(null);
        if (type === "checkbox") {
            setForm((prev) => ({ ...prev, [name]: checked }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleRoleToggle = (role) => {
        setForm((prev) => ({
            ...prev,
            roles: [role], // tek seçim
        }));
    };

    const handleUserTypeChange = (type) => {
        setUserType(type);
    };

    const getAllowedRoles = () => {
        if (userType === "account") {
            return ["ROLE_ACCOUNT_ADMIN", "ROLE_ACCOUNT_ACCOUNTANT"];
        } else if (userType === "company") {
            return [
                "ROLE_COMPANY_ADMIN",
                "ROLE_COMPANY_ACCOUNTANT",
                "ROLE_COMPANY_SALESPERSON",
                "ROLE_COMPANY_CASHIER",
            ];
        } else if (userType === "super") {
            return ["ROLE_SUPER_ADMIN"];
        }
        return [];
    };

    const roleDisplayNames = {
        ROLE_ACCOUNT_ADMIN: "CARİ/BAYİ ADMIN",
        ROLE_ACCOUNT_ACCOUNTANT: "CARİ/BAYİ MUHASEBE",
        ROLE_COMPANY_ADMIN: "FİRMA ADMIN",
        ROLE_COMPANY_ACCOUNTANT: "FİRMA MUHASEBE",
        ROLE_COMPANY_SALESPERSON: "FİRMA SATIŞ",
        ROLE_COMPANY_CASHIER: "FİRMA KASİYER",
        ROLE_SUPER_ADMIN: "SUPER ADMIN",
    };

    const allowedRoles = getAllowedRoles();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        // Basit validasyonlar
        if (!form.username.trim()) {
            setSaving(false);
            return toast.error("Ad soyad gereklidir");
        }
        if (!form.email.trim()) {
            setSaving(false);
            return toast.error("E-posta gereklidir");
        }
        if (userType === "company" && !form.companyId) {
            setSaving(false);
            return toast.error("Firma seçimi gereklidir");
        }
        if (userType === "account" && !form.accountId) {
            setSaving(false);
            return toast.error("Cari/Bayi hesap seçimi gereklidir");
        }
        if (userType === "super" && !form.companyId) {
            setSaving(false);
            return toast.error("Super admin için Tahsilat360 şirketi seçilmelidir");
        }
        if (form.roles.length === 0) {
            setSaving(false);
            return toast.error("Bir rol seçilmelidir");
        }

        try {
            const payload = {
                phoneNumber: form.phoneNumber || undefined,
                username: form.username,
                email: form.email,
                password: form.password?.trim() === "" ? null : form.password,
                roles: form.roles,
                twoFaEnabled: form.twoFaEnabled,
                enabled: form.enabled,
            };

            if (userType === "company" || userType === "super") {
                payload.companyId = form.companyId || getTahsilat360Id();
            } else if (userType === "account") {
                payload.accountId = form.accountId;
            }

            await axios.post(API.create_user, payload, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            toast.success("Kullanıcı başarıyla oluşturuldu!");
            setTimeout(() => navigate("/users"), 1200);
        } catch (err) {
            const msg =
                err.response?.data?.message || "Kullanıcı oluşturulurken bir hata oluştu";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    // Yüklenme gösterimi (kullanıcı bilgisi kritikse)
    if (loadingUserInfo) {
        return (
            <div className="page-content text-center p-5">
                <Spinner size="lg" color="primary" />
                <h6 className="mt-3 text-muted">Yükleniyor...</h6>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content page-common">
                <div className="container-fluid">

                    {/* ── Page Header ── */}
                    <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <BackButton />
                            <div>
                                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                    Yeni Kullanıcı
                                </h5>
                                <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                    Kullanıcı Oluşturma Sayfası
                                </span>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <button
                                className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                                style={{ height: 34, fontSize: ".8rem" }}
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? (<><Spinner size="sm" className="me-1" /> Oluşturuluyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Kullanıcı Oluştur</>)}
                            </button>
                        </div>
                    </div>

                    <Row>
                        <Col lg={12}>
                            <Card className="detail-card">
                                <CardHeader>
                                    <div className="section-title">
                                        <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                            <i className="bx bx-user-plus" />
                                        </div>
                                        Kullanıcı Bilgileri
                                    </div>
                                </CardHeader>
                                <CardBody className="p-4">
                                    {error && (
                                        <Alert color="danger" className="border-0 rounded-4">
                                            <i className="bx bx-error me-2"></i>
                                            {error}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        {/* Kullanıcı Tipi */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Kullanıcı Tipi</h6>
                                            <div className="d-flex flex-wrap gap-3">

                                                {/* Firma Kullanıcısı */}
                                                <div
                                                    className={`p-3 border rounded d-flex align-items-center ${
                                                        userType === "company"
                                                            ? "border-primary bg-light"
                                                            : "border-secondary"
                                                    }`}
                                                    style={{ cursor: "pointer", minWidth: 220 }}
                                                    onClick={() => handleUserTypeChange("company")}
                                                >
                                                    <div
                                                        className="me-3"
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "50%",
                                                            border: `2px solid ${
                                                                userType === "company" ? "#0A316E" : "#6c757d"
                                                            }`,
                                                            backgroundColor:
                                                                userType === "company" ? "#0A316E" : "transparent",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {userType === "company" && (
                                                            <div
                                                                style={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: "white",
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    transform: "translate(-50%, -50%)",
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">Firma Kullanıcısı</div>
                                                        <small className="text-muted">
                                                            Firma bünyesinde çalışan kullanıcı
                                                        </small>
                                                    </div>
                                                </div>

                                                {/* Cari/Bayi Kullanıcı */}
                                                <div
                                                    className={`p-3 border rounded d-flex align-items-center ${
                                                        userType === "account"
                                                            ? "border-primary bg-light"
                                                            : "border-secondary"
                                                    }`}
                                                    style={{ cursor: "pointer", minWidth: 220 }}
                                                    onClick={() => handleUserTypeChange("account")}
                                                >
                                                    <div
                                                        className="me-3"
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "50%",
                                                            border: `2px solid ${
                                                                userType === "account" ? "#0A316E" : "#6c757d"
                                                            }`,
                                                            backgroundColor:
                                                                userType === "account" ? "#0A316E" : "transparent",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {userType === "account" && (
                                                            <div
                                                                style={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: "white",
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    transform: "translate(-50%, -50%)",
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">Cari/Bayi Kullanıcı</div>
                                                        <small className="text-muted">
                                                            Cari/Bayi hesaba bağlı kullanıcı
                                                        </small>
                                                    </div>
                                                </div>

                                                {/* Super Admin */}
                                                {canCreateSuperAdmin() && (
                                                    <div
                                                        className={`p-3 border rounded d-flex align-items-center ${
                                                            userType === "super"
                                                                ? "border-primary bg-light"
                                                                : "border-secondary"
                                                        }`}
                                                        style={{ cursor: "pointer", minWidth: 220 }}
                                                        onClick={() => handleUserTypeChange("super")}
                                                    >
                                                        <div
                                                            className="me-3"
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: "50%",
                                                                border: `2px solid ${
                                                                    userType === "super" ? "#0A316E" : "#6c757d"
                                                                }`,
                                                                backgroundColor:
                                                                    userType === "super" ? "#0A316E" : "transparent",
                                                                position: "relative",
                                                            }}
                                                        >
                                                            {userType === "super" && (
                                                                <div
                                                                    style={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: "50%",
                                                                        backgroundColor: "white",
                                                                        position: "absolute",
                                                                        top: "50%",
                                                                        left: "50%",
                                                                        transform: "translate(-50%, -50%)",
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold">Super Admin</div>
                                                            <small className="text-muted">
                                                                Sistem yöneticisi kullanıcı
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* İlişkilendirme */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">İlişkilendirme</h6>
                                            <Row className="g-3">
                                                {(userType === "company" || userType === "super") && (
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">
                                                                Firma <span className="text-danger">*</span>
                                                            </Label>
                                                            <Input
                                                                type="select"
                                                                name="companyId"
                                                                value={
                                                                    userType === "super"
                                                                        ? getTahsilat360Id()
                                                                        : form.companyId
                                                                }
                                                                onChange={handleChange}
                                                                className="rounded-3"
                                                                disabled={
                                                                    loadingCompanies || userType === "super"
                                                                }
                                                                style={
                                                                    userType === "super"
                                                                        ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                                                                        : {}
                                                                }
                                                            >
                                                                <option value="">
                                                                    {loadingCompanies
                                                                        ? "Firmalar yükleniyor..."
                                                                        : "Firma seçiniz"}
                                                                </option>
                                                                {companies.map((company) => (
                                                                    <option key={company.id} value={company.id}>
                                                                        {company.companyName}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                            {userType === "super" && (
                                                                <small className="text-muted">
                                                                    Super Admin otomatik olarak Tahsilat360’a atanır
                                                                </small>
                                                            )}
                                                        </FormGroup>
                                                    </Col>
                                                )}

                                                {userType === "account" && (
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">
                                                                Cari/Bayi Hesap <span className="text-danger">*</span>
                                                            </Label>
                                                            <Input
                                                                type="select"
                                                                name="accountId"
                                                                value={form.accountId}
                                                                onChange={handleChange}
                                                                className="rounded-3"
                                                                disabled={loadingAccounts}
                                                            >
                                                                <option value="">
                                                                    {loadingAccounts
                                                                        ? "Cari/Bayi hesaplar yükleniyor..."
                                                                        : "Cari/Bayi hesap seçiniz"}
                                                                </option>
                                                                {accounts.map((a) => (
                                                                    <option key={a.id} value={a.id}>
                                                                        {a.accountName}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>

                                        {/* Kimlik & İletişim */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Kişisel Bilgiler</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Ad Soyad <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            name="username"
                                                            value={form.username}
                                                            onChange={(e) =>
                                                                setForm((prev) => ({
                                                                    ...prev,
                                                                    username: toTitleCaseTR(e.target.value),
                                                                }))
                                                            }
                                                            maxLength={50}
                                                            className="rounded-3"
                                                            placeholder="Ad soyad girin"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            E-posta <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="email"
                                                            name="email"
                                                            value={form.email}
                                                            onChange={handleChange}
                                                            maxLength={100}
                                                            className="rounded-3"
                                                            placeholder="E-posta adresi"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <PhoneInput
                                                            value={form.phoneNumber}
                                                            onChange={handleChange}
                                                            name="phoneNumber"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Parola</Label>
                                                        <Input
                                                            type="password"
                                                            name="password" // 'password_field_ghk' yerine 'password' yapın
                                                            value={form.password}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            placeholder="Boş bırakırsanız otomatik oluşturulur"
                                                            autoComplete="new-password"
                                                            spellCheck={false}
                                                            autoCorrect="off"
                                                            inputMode="text"
                                                        />
                                                        <small className="text-muted">
                                                            Boş bırakılırsa sistem otomatik parola oluşturur ve e-posta ile iletir
                                                        </small>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Roller */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Roller</h6>
                                            <div className="d-flex flex-wrap gap-2">
                                                {allowedRoles.map((role) => (
                                                    <Button
                                                        key={role}
                                                        color={form.roles.includes(role) ? "primary" : "secondary"}
                                                        size="sm"
                                                        outline={!form.roles.includes(role)}
                                                        className="rounded-pill"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleRoleToggle(role);
                                                        }}
                                                    >
                                                        {roleDisplayNames[role] || role}
                                                    </Button>
                                                ))}
                                            </div>
                                            <small className="text-muted d-block mt-2">
                                                {userType === "account"
                                                    ? "Cari/Bayi kullanıcılar için geçerli roller"
                                                    : userType === "company"
                                                        ? "Firma kullanıcıları için geçerli roller"
                                                        : "Super Admin rolü"}
                                            </small>
                                        </div>

                                        {/* Güvenlik & Durum */}
                                        <div
                                            className="border rounded-4 p-4 mb-4"
                                            style={{ backgroundColor: "#f9f9f9" }}
                                        >
                                            <h6 className="mb-3 fw-semibold">Güvenlik ve Durum</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            İki Faktörlü Doğrulama
                                                        </Label>
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="toggle-switch"
                                                                onClick={() =>
                                                                    setForm((prev) => ({
                                                                        ...prev,
                                                                        twoFaEnabled: !prev.twoFaEnabled,
                                                                    }))
                                                                }
                                                                style={{
                                                                    width: 50,
                                                                    height: 24,
                                                                    backgroundColor: form.twoFaEnabled
                                                                        ? "#0A316E"
                                                                        : "#6c757d",
                                                                    borderRadius: 12,
                                                                    position: "relative",
                                                                    cursor: "pointer",
                                                                    transition: "background-color 0.2s",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        backgroundColor: "white",
                                                                        borderRadius: "50%",
                                                                        position: "absolute",
                                                                        top: 2,
                                                                        left: form.twoFaEnabled ? 28 : 2,
                                                                        transition: "left 0.2s",
                                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="ms-3">
                                <strong>{form.twoFaEnabled ? "Etkin" : "Pasif"}</strong>
                                <br />
                                <small className="text-muted">
                                  {form.twoFaEnabled
                                      ? "Ekstra güvenlik aktif olacak"
                                      : "Sadece şifre ile doğrulama"}
                                </small>
                              </span>
                                                        </div>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">
                                                            Kullanıcı Durumu
                                                        </Label>
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="toggle-switch"
                                                                onClick={() =>
                                                                    setForm((prev) => ({
                                                                        ...prev,
                                                                        enabled: !prev.enabled,
                                                                    }))
                                                                }
                                                                style={{
                                                                    width: 50,
                                                                    height: 24,
                                                                    backgroundColor: form.enabled ? "#28a745" : "#6c757d",
                                                                    borderRadius: 12,
                                                                    position: "relative",
                                                                    cursor: "pointer",
                                                                    transition: "background-color 0.2s",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        backgroundColor: "white",
                                                                        borderRadius: "50%",
                                                                        position: "absolute",
                                                                        top: 2,
                                                                        left: form.enabled ? 28 : 2,
                                                                        transition: "left 0.2s",
                                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="ms-3">
                                <strong>{form.enabled ? "Aktif" : "Pasif"}</strong>
                                <br />
                                <small className="text-muted">
                                  {form.enabled
                                      ? "Kullanıcı sisteme giriş yapabilir"
                                      : "Kullanıcı devre dışı bırakılacak"}
                                </small>
                              </span>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Aksiyonlar */}
                                        <div className="d-flex justify-content-end gap-2">
                                            <button type="button" className="btn btn-light btn-rounded px-4" style={{ height: 34, fontSize: ".8rem" }} onClick={() => navigate(-1)} disabled={saving}>
                                                İptal
                                            </button>
                                            <button type="submit" className="btn btn-navy btn-rounded px-4 d-flex align-items-center gap-1" style={{ height: 34, fontSize: ".8rem" }} disabled={saving}>
                                                {saving ? (<><Spinner size="sm" className="me-1" /> Oluşturuluyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Kullanıcı Oluştur</>)}
                                            </button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </React.Fragment>
    );
};

export default CreateUserPage;